import React, { useEffect, useState, useRef , useId } from "react";
import { X } from "lucide-react";
import "./PaginatedSearchSelect.css";

const PaginatedSearchSelect = ({
 
  id,
  name,
  alt,
  api,
  GetPaginatedData,
  PostPaginatedData,
  method = "GET", // ✅ allow GET or POST
  getOptionLabel,
  onSelect,
  value = null,
  placeholder = "Search...",
  disabled = false,
  allowCustom = false,
  customOptionLabel = (value) => `Add "${value}"`,
  style,
  filterFn, // ✅ filter function
  extraOptions = [], // ✅ extra static options
  ...props
}) => {
   /* ---------- Utility: Generate Safe ID ---------- */

const generateSafeId = (id, name, alt, placeholder, ariaLabel, reactId) => {
  if (id) return id;

  const source = name || alt || placeholder || ariaLabel || reactId;

  return source
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
    .replace(/^-+|-+$/g, ""); // ✅ remove leading/trailing dashes
};

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [selectedValue, setSelectedValue] = useState(null);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const reactId = useId();
  const inputValue = (value || selectedValue) ? getOptionLabel(value || selectedValue) : searchTerm;

const generatedId = generateSafeId(
  id,
  name,
  alt,
  placeholder,
  props?.["aria-label"],
  reactId
);
 

  const updateDropdownPosition = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target) && 
          dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClick);
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      updateDropdownPosition();
    }
    return () => {
      document.removeEventListener("mousedown", handleClick);
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    setLoading(true);

    const fetchData = async () => {
      try {
        let res;
        if (method === "POST" && PostPaginatedData) {
          res = await PostPaginatedData(api, { page, search: searchTerm });
        } else if (method === "GET" && GetPaginatedData) {
          res = await GetPaginatedData(api, { page, search: searchTerm });
        } else {
          throw new Error("No valid data fetcher provided");
        }

        let data = res.data || [];
        if (filterFn) {
          data = data.filter(filterFn);
        }
        setOptions(data);
        setTotalCount(res.totalCount || 0);
        setHasNextPage(Boolean(res.nextPage));
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, page, searchTerm, dropdownOpen, filterFn, method, GetPaginatedData, PostPaginatedData]);

  // useEffect(() => {
  //   if (value) {
  //     setSelectedValue(value);
  //   }
  // }, [value]);

  useEffect(() => {
  if (value) {
    setSelectedValue(value);
    setSearchTerm(getOptionLabel(value));
  } else {
    setSelectedValue(null);
    setSearchTerm("");
  }
}, [value, getOptionLabel]);

  useEffect(() => {
    if (dropdownOpen && (value || selectedValue)) {
      setSearchTerm(getOptionLabel(value || selectedValue));
    }
  }, [dropdownOpen, value, selectedValue, getOptionLabel]);

  const handleDropdownScroll = (e) => {
    e.stopPropagation();
  };

  const trimmedSearchTerm = searchTerm.trim();
  const hasExactOption = options.some(
    (option) => getOptionLabel(option).trim().toLowerCase() === trimmedSearchTerm.toLowerCase()
  );
  const estimatedTotalPages = options.length
    ? Math.max(page, Math.ceil(totalCount / options.length))
    : page;

  return (
    <>
      <div className="paginated-search-select" ref={ref} style={style}>
        <div className="pss-input-wrapper">
          <input
            // id="search-input"
              id={generatedId}
            data-testid={generatedId}
            className="pss-input"
            type="text"
            placeholder=" "
            disabled={disabled}
            autoComplete="off"
            value={inputValue}
            onFocus={() => {
              setDropdownOpen(true);
              updateDropdownPosition();
            }}
            onChange={(e) => {
              const newValue = e.target.value;
              setSearchTerm(newValue);
              setPage(1);
              if (value || selectedValue) {
                setSelectedValue(null);
                onSelect(null);
              }
            }}
          />
          <label htmlFor={generatedId} className="pss-label">
            {placeholder}
          </label>
          {inputValue && (
            <span
              onClick={() => {
                setSearchTerm("");
                setSelectedValue(null);
                onSelect(null);
              }}
              className="pss-clear"
            >
              <X size={16} />
            </span>
          )}
        </div>
      </div>

      {dropdownOpen && (
        <div 
          ref={dropdownRef}
          className="pss-dropdown"
          style={{
            top: `${dropdownPosition.top + 4}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
          onScroll={handleDropdownScroll}
        >
          {loading && <div className="pss-loading">Loading…</div>}
          {!loading && options.length === 0 && !allowCustom && (
            <div className="pss-no-results">No results</div>
          )}

          {!loading && allowCustom && trimmedSearchTerm && !hasExactOption && (
            <button
              type="button"
              className="pss-option pss-extra-option"
              onClick={() => {
                const customOption = { id: `custom-${trimmedSearchTerm}`, title: trimmedSearchTerm, isCustom: true };
                setSelectedValue(customOption);
                onSelect(customOption);
                setDropdownOpen(false);
                setSearchTerm("");
                setPage(1);
              }}
            >
              {customOptionLabel(trimmedSearchTerm)}
            </button>
          )}

          {/* ✅ render extra static options */}
          {extraOptions.map((opt, idx) => (
            <button
              key={`extra-${idx}`}
              type="button"
              className="pss-option pss-extra-option"
              onClick={() => {
                setSelectedValue(opt);
                onSelect(opt);
                setDropdownOpen(false);
                setSearchTerm("");
                setPage(1);
              }}
            >
              {opt.label}
            </button>
          ))}

          {options.map((opt, idx) => (
            <button
              key={opt.id || idx}
              type="button"
              className="pss-option"
              onClick={() => {
                setSelectedValue(opt);
                onSelect(opt);
                setDropdownOpen(false);
                setSearchTerm("");
                setPage(1);
              }}
            >
              {getOptionLabel(opt)}
            </button>
          ))}

          {!loading && (page > 1 || hasNextPage) && (
            <div className="pss-pagination">
              <button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>
                Previous
              </button>
              <span>{page} / {estimatedTotalPages}</span>
              <button type="button" disabled={!hasNextPage} onClick={() => setPage((current) => current + 1)}>
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PaginatedSearchSelect;
